'use client';

import { useEffect, useState, useRef } from 'react';
import { useMarketplaceStore, type MarketplaceConfig } from '@/store/marketplace-store';
import { storageService } from '@/services/storage.service';
import { API } from '@/constants/api';

interface ClientInitializerProps {
  initialData: MarketplaceConfig | null;
}

/**
 * Client-side marketplace initializer component
 * Hydrates the marketplace store with server-side fetched data
 * Falls back to client-side initialization if no server data is available
 */
export function ClientMarketplaceInitializer({ initialData }: ClientInitializerProps) {
  const store = useMarketplaceStore();
  const [initialized, setInitialized] = useState(false);
  const [hydrateAttempted, setHydrateAttempted] = useState(false);
  const initializationInProgress = useRef(false);
  
  // We now use a single effect to handle all initialization logic
  useEffect(() => {
    // Skip during SSR
    if (typeof window === 'undefined') return;
    
    // Skip if already attempted
    if (hydrateAttempted) return;
    
    // Guard against store being undefined
    if (!store) {
      console.error('[ClientInit] Store not available');
      setHydrateAttempted(true);
      return;
    }
    
    // Skip if already initialized
    if (store.isInitialized) {
      console.log('[ClientInit] Store already initialized, skipping hydration');
      setInitialized(true);
      setHydrateAttempted(true);
      return;
    }
    
    // Prevent multiple initialization attempts
    if (initializationInProgress.current) {
      console.log('[ClientInit] Initialization already in progress, skipping');
      return;
    }
    
    const initializeStore = async () => {
      initializationInProgress.current = true;
      
      try {
        if (initialData) {
          console.log('[ClientInit] Hydrating marketplace store with SSR data');
          
          if (typeof store.hydrateFromSSR === 'function') {
            // Hydrate the store with SSR data
            store.hydrateFromSSR(initialData);
            console.log('[ClientInit] Store hydrated with SSR data successfully');
            setInitialized(true);
          } else if (typeof store.initialize === 'function') {
            // Fall back to initialize if hydrateFromSSR is missing
            console.log('[ClientInit] Store missing hydrateFromSSR method, using initialize');
            await store.initialize();
            setInitialized(true);
          } else if (typeof store.fetchConfig === 'function') {
            // Last resort - use fetchConfig
            console.log('[ClientInit] Using fetchConfig method as last resort');
            await store.fetchConfig(false); // Don't bypass cache - use normal flow
            setInitialized(true);
          }
        } else {
          console.log('[ClientInit] No SSR data available, using standard initialization');
          
          if (typeof store.initialize === 'function') {
            await store.initialize();
            setInitialized(true);
          } else if (typeof store.fetchConfig === 'function') {
            await store.fetchConfig(false);  // Don't bypass cache - use normal flow
            setInitialized(true);
          }
        }
      } catch (error) {
        console.error('[ClientInit] Error during store initialization:', error);
        
        // Even if initialization fails, mark as attempted to prevent infinite loops
        if (!store.isInitialized && typeof store.isInitialized !== 'undefined') {
          console.log('[ClientInit] Marking store as initialized to prevent loops');
          // @ts-ignore - Emergency override to prevent loops
          store.isInitialized = true;
        }
      } finally {
        setHydrateAttempted(true);
        initializationInProgress.current = false;
      }
    };
    
    // Execute initialization
    initializeStore();
    
  }, [initialData, store, hydrateAttempted]);
  
  // No visible UI
  return null;
}
